<template>
  <div class="rm-screen wm-inv">
    <div class="rm-chip-row wm-status-row" role="group" :aria-label="$t('status')">
      <button
        v-for="chip in statusChips"
        :key="chip.value"
        type="button"
        class="rm-chip"
        :class="{ 'rm-chip--active': stockStatusFilter === chip.value }"
        :data-status="chip.value"
        :aria-pressed="String(stockStatusFilter === chip.value)"
        @click="stockStatusFilter = chip.value"
      >
        {{ chip.label }}
        <b class="rm-num">{{ chip.count }}</b>
      </button>
    </div>

    <div v-if="!loading && allRows.length" class="rm-stats rm-stats--pair">
      <div v-for="kpi in shownKpis" :key="kpi.key" class="rm-stat" :class="kpi.tone ? `rm-stat--${kpi.tone}` : ''">
        <span class="rm-stat__label">{{ kpi.label }}</span>
        <span class="rm-stat__value rm-num">{{ kpi.value }}</span>
      </div>
    </div>

    <div v-if="loading" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>
    <div v-else-if="!rows.length" class="rm-empty">
      <v-icon size="40" color="#8a817a">mdi-archive-outline</v-icon>
      <b>{{ emptyTitle }}</b>
      <span v-if="emptyText">{{ emptyText }}</span>
    </div>

    <template v-else>
      <section v-for="group in visibleGroups" :key="group.key" class="wm-group">
        <h2 v-if="group.name" class="rm-section-title">{{ group.name }} · {{ group.total }}</h2>
        <div class="rm-card">
          <ul class="rm-list">
            <li v-for="row in group.rows" :key="row.key" class="wm-inv-row">
              <span class="wm-inv-row__text">
                <span class="wm-inv-row__name">{{ row.name }}</span>
                <span class="rm-muted rm-num">
                  {{ row.code || "—" }}<template v-if="row.min"> · {{ $t("minimum_quantity") }}: {{ qty(row.min) }} {{ row.measure }}</template>
                </span>
              </span>
              <span class="wm-inv-row__side">
                <span class="wm-inv-row__qty rm-num" :class="`is-${row.status}`">
                  {{ qty(row.quantity) }}<span class="wm-unit">{{ row.measure }}</span>
                </span>
                <span v-if="row.status === 'low'" class="wm-pill is-low">
                  <v-icon size="14">mdi-alert-outline</v-icon>
                  {{ $t("warehouse_shell.below_minimum") }}
                </span>
                <span v-else-if="row.status === 'out'" class="wm-pill is-out">
                  <v-icon size="14">mdi-minus-circle-outline</v-icon>
                  {{ $t("out_of_stock") }}
                </span>
              </span>
            </li>
          </ul>
        </div>
      </section>

      <div v-if="hiddenCount > 0" class="wm-more">
        <span class="rm-muted rm-num">{{ $t("warehouse_shell.shown_of", { shown: shownCount, total: rows.length }) }}</span>
        <button type="button" class="rm-btn rm-btn--sm" data-action="more" @click="showMore">
          {{ $t("warehouse_shell.show_more") }}
        </button>
      </div>
    </template>

    <div class="wm-bar">
      <button type="button" class="rm-btn rm-btn--primary" data-action="actions" @click="actionsSheet = true">
        <v-icon size="20" color="#ffffff">mdi-plus-circle-outline</v-icon>
        {{ $t("actions") }}
      </button>
    </div>

    <ui-sheet v-model="actionsSheet" :label="$t('actions')">
      <ul class="rm-list rm-actions">
        <li v-for="action in actions" :key="action.key">
          <button type="button" class="rm-actions__row" :data-action="action.key" @click="runStockAction(action)">
            <v-icon size="22" :color="action.color">{{ action.icon }}</v-icon>
            {{ action.label }}
          </button>
        </li>
      </ul>
    </ui-sheet>

    <warehouse-purchase-order-dialog v-model="purchaseOrderDialog" :edited-item="editedItem" />
    <warehouse-entry-grocery-dialog v-model="purchaseItemDialog" :edited-item="editedItem" @success="triggerRefresh" />
    <warehouse-waybill-dialog v-model="waybillDialog" :edited-item="editedItem" @success="triggerRefresh" />
    <warehouse-write-off-dialog v-model="writeOffDialog" :edited-item="editedItem" @success="triggerRefresh" />
    <warehouse-return-dialog v-model="returnDialog" :edited-item="editedItem" @success="triggerRefresh" />
  </div>
</template>

<script>
import "../warehouseMobile.scss";
import WarehouseDesktopInventory from "../../screens/WarehouseDesktopInventory.vue";

/**
 * Stanje magacina na telefonu: izbor stanja sa brojačima, artikli kao redovi (grupisani po magacinu kad ih je više),
 * radnje (naruči, dodaj, izdaj, povrat, otpis) u donjoj kartici. Nasleđuje sve iz računarskog ekrana (učitavanje,
 * filtriranje, štampa, Excel, dijalozi); menja se samo prikaz.
 */
export default {
  name: "WarehouseMobileInventory",
  extends: WarehouseDesktopInventory,
  data: () => ({
    actionsSheet: false,
  }),
  computed: {
    actions() {
      return [
        { key: "request", label: this.$t("warehouse.request_quote"), icon: "mdi-file-document-outline", color: "#1c1917", run: () => this.orderGroceries(false) },
        { key: "purchase", label: this.$t("warehouse.purchase_order"), icon: "mdi-cart-arrow-down", color: "#1c1917", run: () => this.orderGroceries(true) },
        { key: "entry", label: this.$t("add_warehouse"), icon: "mdi-plus", color: "#1c1917", run: () => this.openDialog("purchaseItemDialog") },
        { key: "release", label: this.$t("warehouse.release"), icon: "mdi-truck-delivery-outline", color: "#1c1917", run: () => this.openDialog("waybillDialog") },
        { key: "return", label: this.$t("warehouse.return"), icon: "mdi-backup-restore", color: "#1c1917", run: () => this.openDialog("returnDialog") },
        { key: "writeoff", label: this.$t("warehouse.write-off"), icon: "mdi-trash-can-outline", color: "#b42318", run: () => this.openDialog("writeOffDialog") },
      ];
    },
  },
  methods: {
    runStockAction(action) {
      this.actionsSheet = false;
      action.run();
    },
  },
};
</script>
